import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const WishlistCard = ({ wishlist, onEdit, onDelete, onDuplicate }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const navigate = useNavigate();

  const handleCardClick = () => {
    // TODO: Implement individual wishlist detail pages in Session 3
    // navigate(`/wishlists/${wishlist.id}`);
    console.log('Wishlist card clicked:', wishlist.id);
  };

  const handleMenuToggle = (e) => {
    e.stopPropagation();
    setIsMenuOpen(!isMenuOpen);
  };

  const handleAction = (e, action) => {
    e.stopPropagation();
    setIsMenuOpen(false);
    action();
  };

  const getPrivacyIcon = () => {
    switch (wishlist.privacy) {
      case 'public': return 'Globe';
      case 'friends': return 'Users';
      case 'family': return 'Home';
      default: return 'Lock';
    }
  };

  const getPrivacyColor = () => {
    switch (wishlist.privacy) {
      case 'public': return 'text-success-600';
      case 'friends': return 'text-secondary-600';
      case 'family': return 'text-warning-600';
      default: return 'text-accent-600';
    }
  };

  return (
    <div
      className="group relative bg-white rounded-2xl shadow-md hover:shadow-warm transition-all duration-300 cursor-pointer hover-lift overflow-hidden"
      onClick={handleCardClick}
    >
      {/* Cover Image */}
      <div className="relative h-48 overflow-hidden">
        <Image
          src={wishlist.coverImage}
          alt={wishlist.name}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

        {/* Quick Actions Overlay */}
        <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <div className="relative">
            <Button
              variant="ghost"
              size="sm"
              iconName="MoreVertical"
              onClick={handleMenuToggle}
              className="bg-white/90 backdrop-blur-sm text-accent-700 hover:bg-white shadow-sm"
            />

            {isMenuOpen && (
              <div className="absolute right-0 top-full mt-2 w-48 bg-white rounded-xl shadow-lg border border-accent-100 py-2 z-10">
                <button
                  onClick={(e) => handleAction(e, onEdit)}
                  className="w-full flex items-center space-x-3 px-4 py-2 text-left text-accent-700 hover:bg-surface transition-colors"
                >
                  <Icon name="Edit" size={16} />
                  <span>Edit Wishlist</span>
                </button>
                <button
                  onClick={(e) => handleAction(e, onDuplicate)}
                  className="w-full flex items-center space-x-3 px-4 py-2 text-left text-accent-700 hover:bg-surface transition-colors"
                >
                  <Icon name="Copy" size={16} />
                  <span>Duplicate</span>
                </button>
                <hr className="my-2 border-accent-100" />
                <button
                  onClick={(e) => handleAction(e, onDelete)}
                  className="w-full flex items-center space-x-3 px-4 py-2 text-left text-error-600 hover:bg-error-50 transition-colors"
                >
                  <Icon name="Trash2" size={16} />
                  <span>Delete</span>
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Privacy Badge */}
        <div className="absolute top-4 left-4">
          <div className={`flex items-center space-x-1 px-2 py-1 bg-white/90 backdrop-blur-sm rounded-lg text-xs font-medium ${getPrivacyColor()}`}>
            <Icon name={getPrivacyIcon()} size={12} />
            <span className="capitalize">{wishlist.privacy}</span>
          </div>
        </div>

        {/* Occasion Badge */}
        {wishlist.occasion && (
          <div className="absolute bottom-4 left-4">
            <div className="flex items-center space-x-1 px-2 py-1 bg-primary/90 backdrop-blur-sm rounded-lg text-xs font-medium text-white">
              <Icon name="Calendar" size={12} />
              <span>{wishlist.occasion}</span>
            </div>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-6">
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1">
            <h3 className="font-heading font-semibold text-lg text-accent-800 mb-1 line-clamp-1">
              {wishlist.name}
            </h3>
            {wishlist.description && (
              <p className="text-sm text-accent-600 line-clamp-2">
                {wishlist.description}
              </p>
            )}
          </div>
        </div>

        {/* Stats */}
        <div className="flex items-center justify-between text-sm text-accent-600 mb-4">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-1">
              <Icon name="Gift" size={14} />
              <span>{wishlist.itemCount} items</span>
            </div>
            {wishlist.views > 0 && (
              <div className="flex items-center space-x-1">
                <Icon name="Eye" size={14} />
                <span>{wishlist.views} views</span>
              </div>
            )}
          </div>
          <div className="text-xs text-accent-500">
            Updated {wishlist.lastUpdated}
          </div>
        </div>

        {/* Priority Distribution */}
        <div className="space-y-2">
          <div className="flex items-center justify-between text-xs text-accent-600">
            <span>Priority Distribution</span>
            <span>{wishlist.completionRate}% fulfilled</span>
          </div>
          <div className="flex space-x-1 h-2">
            <div
              className="bg-error-400 rounded-full"
              style={{ width: `${(wishlist.priorities.mustHave / wishlist.itemCount) * 100}%` }}
            />
            <div
              className="bg-warning-400 rounded-full"
              style={{ width: `${(wishlist.priorities.wouldLove / wishlist.itemCount) * 100}%` }}
            />
            <div
              className="bg-success-400 rounded-full"
              style={{ width: `${(wishlist.priorities.justIdeas / wishlist.itemCount) * 100}%` }}
            />
          </div>
          <div className="flex items-center justify-between text-xs">
            <div className="flex items-center space-x-3">
              <div className="flex items-center space-x-1">
                <div className="w-2 h-2 bg-error-400 rounded-full" />
                <span className="text-accent-600">Must Have</span>
              </div>
              <div className="flex items-center space-x-1">
                <div className="w-2 h-2 bg-warning-400 rounded-full" />
                <span className="text-accent-600">Would Love</span>
              </div>
              <div className="flex items-center space-x-1">
                <div className="w-2 h-2 bg-success-400 rounded-full" />
                <span className="text-accent-600">Just Ideas</span>
              </div>
            </div>
          </div>
        </div>

        {/* Collaborators */}
        {wishlist.collaborators && wishlist.collaborators.length > 0 && (
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <div className="flex -space-x-2">
                {wishlist.collaborators.slice(0, 3).map((collaborator, index) => (
                  <Image
                    key={index}
                    src={collaborator.avatar}
                    alt={collaborator.name}
                    className="w-6 h-6 rounded-full border-2 border-white"
                  />
                ))}
                {wishlist.collaborators.length > 3 && (
                  <div className="w-6 h-6 bg-accent-100 rounded-full border-2 border-white flex items-center justify-center">
                    <span className="text-xs text-accent-600 font-medium">
                      +{wishlist.collaborators.length - 3}
                    </span>
                  </div>
                )}
              </div>
              <span className="text-xs text-accent-600">
                {wishlist.collaborators.length} collaborator{wishlist.collaborators.length !== 1 ? 's' : ''}
              </span>
            </div>
          </div>
        )}

        {/* Show basic info if no collaborators */}
        {(!wishlist.collaborators || wishlist.collaborators.length === 0) && (
          <div className="text-xs text-accent-500">
            Private wishlist
          </div>
        )}
      </div>
    </div>
  );
};

export default WishlistCard;